import axios from "axios";

// Host only — never put /api on baseURL. Axios treats paths that start with "/" as
// absolute from the origin, so baseURL ".../api" + "/venture-project/..." becomes
// ".../venture-project/..." (drops /api). Always call with /api/... on the path instead.
const DEFAULT_PROD_API_HOST = "https://api.buyrentsale.com";
const DEFAULT_PROD_COMMUNICATION_BASE_URL = "https://api.buyrentsale.com/api/communication";

const normalizeApiHost = (url) => {
  const cleaned = String(url || "").trim().replace(/\/$/, "");
  if (!cleaned) {
    return cleaned;
  }

  return cleaned.replace(/\/api$/i, "");
};

const resolveApiBaseUrl = () => {
  const envBaseUrl = import.meta.env.VITE_API_BASE_URL;

  if (envBaseUrl) {
    return normalizeApiHost(envBaseUrl);
  }

  // Local: same-origin so /api/... hits the Vite proxy
  if (import.meta.env.DEV) {
    return "";
  }

  return DEFAULT_PROD_API_HOST;
};

const resolveCommunicationBaseUrl = () => {
  const envBaseUrl = import.meta.env.VITE_COMMUNICATION_API_BASE_URL;

  if (envBaseUrl) {
    return envBaseUrl.replace(/\/$/, "");
  }

  if (import.meta.env.DEV) {
    return "/communication";
  }

  return DEFAULT_PROD_COMMUNICATION_BASE_URL;
};

const API_BASE_URL = resolveApiBaseUrl();
const COMMUNICATION_API_BASE_URL = resolveCommunicationBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000
});

const resolveApiPath = (path) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (normalizedPath === "/api" || normalizedPath.startsWith("/api/")) {
    return normalizedPath;
  }
  return `/api${normalizedPath}`;
};

export const postApi = (path, data = null) => apiClient.post(resolveApiPath(path), data);

const communicationApiClient = axios.create({
  baseURL: COMMUNICATION_API_BASE_URL,
  timeout: 15000
});

export const postCommunicationApi = (path, data = null) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return communicationApiClient.post(normalizedPath, data);
};

export const sendInvestmentOtp = async ({ otp, mobile }) => {
  const response = await communicationApiClient.post("/sendSMSPostReq", { otp, mobile });
  return response.data;
};

export const submitInvestmentRequirement = async (formData) => {
  const response = await communicationApiClient.post("/send-post-requirement", formData);
  return response.data;
};

const isLikelyReraNo = (value) => /^P\d{5,}$/i.test(value.replace(/\s+/g, ""));
const isLikelyLpNumber = (value) => /\d+\/\d{4}\/\w+/i.test(value);

const parsePrefixedSearch = (value) => {
  const match = value.match(/^(ventureName|builderName|reraNo|lpNumber|searchText)\s*[:=]\s*(.+)$/i);

  if (!match) {
    return null;
  }

  return {
    key: match[1],
    query: match[2].trim()
  };
};

const getSearchParams = ({ query, pageNumber, pageSize, filters = {} }) => {
  const params = {
    pageNumber,
    pageSize
  };

  if (query) {
    const normalizedQuery = query.trim();
    const prefixedSearch = parsePrefixedSearch(normalizedQuery);

    if (prefixedSearch?.query) {
      params[prefixedSearch.key] = prefixedSearch.query;
    } else if (isLikelyReraNo(normalizedQuery)) {
      params.reraNo = normalizedQuery;
    } else if (isLikelyLpNumber(normalizedQuery)) {
      params.lpNumber = normalizedQuery;
    } else {
      params.searchText = normalizedQuery;
    }
  }

  // UI filter labels → API param names. Sending unknown keys (e.g. possession)
  // can make the API return 0 rows even when other filters are valid.
  const filterDefaults = {
    location: "All Locations",
    budget: "Any Budget",
    propertyType: "All Types",
    approval: "Any Approval",
    builderName: "All Builders",
    possession: "Any Time"
  };

  const filterKeyMap = {
    location: "location",
    propertyType: "propertyType",
    approval: "approval",
    builderName: "builderName",
    possession: "projectStatus"
  };

  Object.entries(filters).forEach(([key, value]) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (!trimmed || trimmed === filterDefaults[key]) {
      return;
    }

    // API Approvals match is exact; "RERA" misses values like "RERA, GHMC".
    // Handle RERA with a client-side contains filter in searchVentures.
    if (key === "approval" && /^rera$/i.test(trimmed)) {
      params.__approvalIncludes = "RERA";
      return;
    }

    const apiKey = filterKeyMap[key] || key;
    // Avoid overwriting a more specific query field already set above.
    if (params[apiKey] == null || params[apiKey] === "") {
      params[apiKey] = trimmed;
    }
  });

  return params;
};

const itemMatchesApprovalIncludes = (item, needle) => {
  const approvals = String(item?.Approvals ?? item?.approvals ?? "").toUpperCase();
  const hasRera = item?.HasRERA === true || item?.hasRera === true;
  const token = String(needle || "").toUpperCase();
  if (!token) {
    return true;
  }
  if (token === "RERA") {
    return hasRera || approvals.includes("RERA");
  }
  return approvals.includes(token);
};

const getSafeErrorMessage = (errorMessage) => {
  if (typeof errorMessage !== "string") {
    return "Unable to fetch ventures right now.";
  }
  return errorMessage;
};

const getPayloadErrorMessage = (payload) => payload?.errorMessage || payload?.ErrorMessage || payload?.message || payload?.Message || payload?.ResponseData?.errorMessage || payload?.ResponseData?.ErrorMessage || "";

const hasErrorFlag = (payload) => {
  const statusCode = Number(payload?.statusCode ?? payload?.StatusCode);
  return payload?.isError === true || payload?.IsError === true || Number.isFinite(statusCode) && statusCode >= 400;
};

const looksLikeBackendErrorMessage = (message) => /does not belong to table|invalid column|sql|exception|server error|internal error/i.test(message);

const normalizeResponse = (payload) => {
  const body = payload?.body || payload?.Body || payload?.responseData || payload?.ResponseData || payload;
  const payloadErrorMessage = getPayloadErrorMessage(payload) || getPayloadErrorMessage(body);
  const shouldThrow = hasErrorFlag(payload) || hasErrorFlag(body) || payloadErrorMessage && looksLikeBackendErrorMessage(payloadErrorMessage);

  if (shouldThrow) {
    throw new Error(getSafeErrorMessage(payloadErrorMessage));
  }

  const list = body?.items || body?.Items || body?.Data || body?.data || payload?.items || payload?.Items || payload?.results || payload?.Results || payload?.ResponseData?.Data || payload;

  const items = Array.isArray(list) ? list : [];
  const totalCountRaw = body?.totalCount || body?.TotalCount || body?.totalRecords || body?.TotalRecords || payload?.totalCount || payload?.TotalCount || payload?.totalRecords || payload?.TotalRecords;
  const totalCount = Number.isFinite(Number(totalCountRaw)) ? Number(totalCountRaw) : items.length;

  return { items, totalCount };
};

export const searchVentures = async ({ query = "", pageNumber = 1, pageSize = 10, filters }) => {
  const params = getSearchParams({ query, pageNumber, pageSize, filters });
  const approvalIncludes = params.__approvalIncludes;
  delete params.__approvalIncludes;

  if (!approvalIncludes) {
    const response = await postApi("/venture-project/search-ventures/", { params });
    return normalizeResponse(response.data);
  }

  // Walk source pages until we can slice the requested page of RERA matches.
  const needed = pageNumber * pageSize;
  const matched = [];
  const fetchSize = Math.max(pageSize * 5, 50);
  let sourcePage = 1;
  let sourceTotal = Infinity;
  const maxSourcePages = 40;

  while (matched.length < needed && sourcePage <= maxSourcePages) {
    const response = await postApi("/venture-project/search-ventures/", {
      params: {
        ...params,
        pageNumber: sourcePage,
        pageSize: fetchSize
      }
    });
    const page = normalizeResponse(response.data);
    sourceTotal = page.totalCount;
    matched.push(...page.items.filter((item) => itemMatchesApprovalIncludes(item, approvalIncludes)));

    if (page.items.length < fetchSize || sourcePage * fetchSize >= sourceTotal) {
      break;
    }
    sourcePage += 1;
  }

  const start = (pageNumber - 1) * pageSize;
  const exhausted = sourcePage * fetchSize >= sourceTotal || matched.length < needed;

  return {
    items: matched.slice(start, start + pageSize),
    // When we exhausted the source, matched.length is exact; otherwise it's a lower bound.
    totalCount: exhausted ? matched.length : Math.max(matched.length, needed + 1)
  };
};
